/**
 * Geometria das Cercas Virtuais (reaproveitado da POC MK-18056).
 *
 * Uma cerca é um círculo geográfico: centro { lat, lng } + raio em metros.
 * A detecção de sobreposição é puramente matemática (sem polígonos), coerente
 * com a decisão de usar círculos por bateria/escala.
 */
const EARTH_RADIUS_M = 6371000

const toRad = (deg) => (deg * Math.PI) / 180

/** Distância em metros entre dois pontos { lat, lng } (Haversine). */
export const haversineMeters = (a, b) => {
  if (!a || !b || a.lat == null || a.lng == null || b.lat == null || b.lng == null) {
    return Infinity
  }
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

/**
 * Dois círculos geográficos se sobrepõem quando a distância entre os centros é
 * MENOR que a soma dos raios (tangentes — borda a borda — não contam como conflito).
 */
export const circlesOverlap = (a, b) => {
  if (!a || !b) return false
  const distance = haversineMeters(a, b)
  if (!Number.isFinite(distance)) return false
  return distance < Number(a.radius || 0) + Number(b.radius || 0)
}

/** Cercas de `list` que se sobrepõem a `target` (ignora a própria pelo `id`). */
export const findOverlaps = (target, list = []) => {
  if (!target || target.lat == null || target.lng == null) return []
  return list.filter((cerca) => cerca && cerca.id !== target.id && circlesOverlap(target, cerca))
}

/**
 * Existe ao menos um par sobreposto em `list`? Sai no primeiro par encontrado.
 *
 * Quem decide QUEM entra na lista é o chamador: participação no conflito é regra de tela (só cerca
 * que ocupa área ativa participa), não de geometria. Existe porque a legenda de conflito da listagem
 * precisa saber se há tracejado na tela, e o único cálculo par-a-par do módulo vivia dentro do
 * render do Leaflet, que não roda no Jest deste repo — aqui a decisão fica testável (MK-21205).
 */
export const anyOverlap = (list = []) => {
  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      // Mesma invariante do `findOverlaps`: cerca nao conflita consigo mesma. Comparar so por
      // indice faria uma lista com item duplicado virar "conflito" e acender a legenda sem
      // sobreposicao nenhuma na tela (achado do Copilot na #561).
      if (list[i]?.id != null && list[i].id === list[j]?.id) continue
      if (circlesOverlap(list[i], list[j])) return true
    }
  }
  return false
}

/** Cerca renderizável no mapa: precisa de coordenada. Status é outra pergunta. */
export const temLocalizacao = (fence) => !!(fence && fence.location && fence.location.lat != null && fence.location.lng != null)

/**
 * Participa do cálculo de conflito de área? Só cerca que ocupa área ATIVA: Ativa e Rascunho.
 *
 * Inativa e Expirada aparecem no mapa da listagem (MK-21205) mas não geram nem recebem tracejado,
 * e o fundamento não é estético: cerca nessas situações não entra na carga de propagação nem
 * persiste evento, então sobreposição com elas não tem consequência operacional nenhuma.
 *
 * Usada pela LISTAGEM. Não serve pras telas de criação/edição, que têm regra própria: ver
 * `estaAtiva` logo abaixo.
 */
export const ocupaAreaAtiva = (fence) => fence?.status === 'ACTIVE' || fence?.status === 'DRAFT'

/**
 * Cerca em operação de fato: só Ativa.
 *
 * A regra de quem serve de referência é POR SUPERFÍCIE, e as duas convivem de propósito:
 *   - listagem: `ocupaAreaAtiva` (Ativa + Rascunho), porque lá o objetivo é mostrar o que EXISTE;
 *   - criação e edição: `estaAtiva`, porque lá o mapa é referência pra posicionar uma cerca, e
 *     rascunho de terceiro não está em operação — só poluía (MK-21209).
 *
 * Não unifique as duas achando que é duplicação: elas responderiam perguntas diferentes, e o próprio
 * histórico mostra a divergência sendo pedida card por card.
 */
export const estaAtiva = (fence) => fence?.status === 'ACTIVE'

/**
 * Teto de cercas que entram no cálculo do foco inicial do mapa (MK-21210).
 *
 * Dez porque o card pede dez, e o card não pede critério de prioridade: "qualquer conjunto de até
 * 10 ativas é válido". Na prática o corte pega as 10 primeiras da ordem que o backend devolve
 * (ENTRIES DESC), ou seja as de maior movimento, que é um subconjunto razoável do que foi pedido.
 */
export const MAX_CERCAS_FOCO = 10

/**
 * Cercas que definem o enquadramento inicial do mapa: até 10 ATIVAS com localização.
 *
 * Existe como função, e não como `.filter().slice()` espalhado, porque as três telas do card
 * (listagem, criação, edição) precisam da MESMA regra e antes cada uma resolvia foco por conta:
 * a listagem chamava `fitToFences`, e o formulário não enquadrava nada — dava `setView` no centro
 * da PRIMEIRA cerca com coordenada (ou da primeira loja, ou Curitiba) com zoom fixo 12. Medido na
 * conta de teste: 1 das 5 Ativas dentro do retângulo do mapa, as outras 4 fora.
 *
 * Status é o de `estaAtiva`: Rascunho, Inativa e Expirada não influenciam o foco, mesmo quando
 * aparecem no mapa (é o caso da listagem, onde Rascunho é desenhado e não conta pro zoom).
 */
export const cercasParaFoco = (fences = []) => (fences || []).filter((f) => temLocalizacao(f) && estaAtiva(f)).slice(0, MAX_CERCAS_FOCO)

/** Shape geométrico que `circlesOverlap`/`anyOverlap` esperam, a partir de uma cerca do contrato. */
export const toCirculo = (fence) => ({
  id: fence?.id,
  lat: fence?.location?.lat,
  lng: fence?.location?.lng,
  radius: Number(fence?.radiusMeters) || 0,
})
